// AdminPage.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import styles from './ChatApp.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;;

const CreateUserForm = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setIsLoading(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/distributor/create_rep`, {
                name, email, password
            });
            setMessage(`Successfully created rep: ${response.data.name} (ID: ${response.data.id})`);
            setName('');
            setEmail('');
            setPassword('');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create sales rep.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.adminCard}>
            <h3 className={styles.adminSectionTitle}>Create New Sales Rep</h3>
            {message && <div className={styles.adminSuccess}>{message}</div>}
            {error && <div className={styles.adminError}>{error}</div>}
            <form onSubmit={handleSubmit} className={styles.adminForm}>
                <div className={styles.formGroup}>
                    <label>Rep Name:</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Sarah Salesrep"
                        required
                        className={styles.loginInput}
                        disabled={isLoading}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Email:</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="e.g., sarah@example.com"
                        required
                        className={styles.loginInput}
                        disabled={isLoading}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Password:</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Set a temporary password"
                        required
                        className={styles.loginInput}
                        disabled={isLoading}
                    />
                </div>
                <button type="submit" className={styles.adminButton} disabled={isLoading}>
                    {isLoading ? 'Creating...' : 'Create Rep'}
                </button>
            </form>
        </div>
    );
};

const CreateStoreForm = ({ paymentTerms = [], onStoreCreated }) => {
    const [name, setName] = useState('');
    const [addressLine1, setAddressLine1] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [terms, setTerms] = useState(paymentTerms[0] || 'NET 30');
    const [taxId, setTaxId] = useState('');
    
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!terms && paymentTerms.length > 0) {
            setTerms(paymentTerms[0]);
        }
    }, [paymentTerms, terms]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setIsLoading(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/distributor/create_store`, {
                name, addressLine1, city, state, zipCode, 
                payment_terms: terms,
                taxId
            });
            setMessage(`Successfully created store: ${response.data.name} (ID: ${response.data.id})`);
            
            setName('');
            setAddressLine1('');
            setCity('');
            setState('');
            setZipCode('');
            setTerms(paymentTerms[0] || 'NET 30');
            setTaxId('');
            
            if (onStoreCreated) onStoreCreated();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create store.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.adminCard}>
            <h3 className={styles.adminSectionTitle}>Create New Store</h3>
            {message && <div className={styles.adminSuccess}>{message}</div>}
            {error && <div className={styles.adminError}>{error}</div>}
            <form onSubmit={handleSubmit} className={styles.adminForm}>
                <div className={styles.formGroup}>
                    <label>Store Name:</label>
                    <input
                        type="text" value={name} onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Main Street Grocers" required
                        className={styles.loginInput} disabled={isLoading}
                    />
                </div>
                
                <div className={styles.formGroup}>
                    <label>Tax ID (Optional):</label>
                    <input
                        type="text" value={taxId} onChange={(e) => setTaxId(e.target.value)}
                        placeholder="e.g., 12-3456789"
                        className={styles.loginInput} disabled={isLoading}
                    />
                </div>

                <div className={styles.formGroup}>
                    <label>Address Line 1:</label>
                    <input
                        type="text" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)}
                        placeholder="e.g., 123 Main St" required
                        className={styles.loginInput} disabled={isLoading}
                    />
                </div>
                 <div className={styles.formGroup}>
                    <label>City:</label>
                    <input
                        type="text" value={city} onChange={(e) => setCity(e.target.value)}
                        placeholder="e.g., Anytown" required
                        className={styles.loginInput} disabled={isLoading}
                    />
                </div>
                 <div className={styles.formGroup}>
                    <label>State:</label>
                    <input
                        type="text" value={state} onChange={(e) => setState(e.target.value)}
                        placeholder="e.g., CA" required
                        className={styles.loginInput} disabled={isLoading}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Zip Code:</label>
                    <input
                        type="text" value={zipCode} onChange={(e) => setZipCode(e.target.value)}
                        placeholder="e.g., 12345" required
                        className={styles.loginInput} disabled={isLoading}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Payment Terms:</label>
                    <select value={terms} onChange={(e) => setTerms(e.target.value)} disabled={isLoading || paymentTerms.length === 0}>
                        {paymentTerms.length > 0 ? (
                            paymentTerms.map(term => (
                                <option key={term} value={term}>{term}</option>
                            ))
                        ) : (
                            <option value="">Loading terms...</option>
                        )}
                    </select>
                </div>
                <button type="submit" className={styles.adminButton} disabled={isLoading}>
                    {isLoading ? 'Creating...' : 'Create Store'}
                </button>
            </form>
        </div>
    );
};

const PaymentTermsManager = ({ terms, onTermsUpdated }) => {
    const [newTerm, setNewTerm] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newTerm.trim()) {
            setError('Term cannot be empty.');
            return;
        }
        setMessage('');
        setError('');
        setIsLoading(true);
        try {
            await axios.post(`${API_BASE_URL}/admin/payment_terms?term=${newTerm}`);
            setMessage(`Successfully added term: ${newTerm}`);
            setNewTerm('');
            if (onTermsUpdated) onTermsUpdated(); // Trigger refetch
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to add term.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.adminCard}>
            <h3 className={styles.adminSectionTitle}>Manage Payment Terms</h3>
            {message && <div className={styles.adminSuccess}>{message}</div>}
            {error && <div className={styles.adminError}>{error}</div>}
            
            <h4 className={styles.adminSubTitle}>Current Terms</h4>
            <div className={styles.assignmentStoreList} style={{ marginBottom: '15px' }}>
                {terms.length > 0 ? terms.map(term => (
                    <span key={term} className={styles.assignmentStoreTag}>{term}</span>
                )) : (
                    <span className={styles.assignmentStoreTagEmpty}>No terms found.</span>
                )}
            </div>

            <h4 className={styles.adminSubTitle}>Add New Term</h4>
            <form onSubmit={handleSubmit} className={styles.adminForm} style={{flexDirection: 'row', gap: '10px'}}>
                <div className={styles.formGroup} style={{flex: 1}}>
                    <input
                        type="text"
                        value={newTerm}
                        onChange={(e) => setNewTerm(e.target.value)}
                        placeholder="e.g., NET 60"
                        required
                        className={styles.loginInput}
                        disabled={isLoading}
                    />
                </div>
                <button type="submit" className={styles.adminButton} disabled={isLoading} style={{width: 'auto', marginTop: 0}}>
                    {isLoading ? 'Adding...' : 'Add Term'}
                </button>
            </form>
        </div>
    );
};

const StoreAssignment = ({ stores, reps, onAssignmentCreated }) => {
    const [assignments, setAssignments] = useState({});
    const [selectedRep, setSelectedRep] = useState('');
    const [selectedStore, setSelectedStore] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchAssignments = async () => {
        setIsLoading(true);
        try {
            const assignmentsRes = await axios.get(`${API_BASE_URL}/distributor/assignments`);
            setAssignments(assignmentsRes.data);
        } catch (err) {
            setError('Failed to load assignment data.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAssignments();
    }, []);

    const handleAssign = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        if (!selectedRep || !selectedStore) {
            setError('Please select both a sales rep and a store.');
            return;
        }
        console.log('Sending to backend:', { rep_id: selectedRep, store_id: selectedStore });
        setIsSubmitting(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/distributor/assign_store`, {
                rep_id: selectedRep,
                store_id: selectedStore
            });
            setMessage(response.data.message);
            fetchAssignments();
            if (onAssignmentCreated) onAssignmentCreated();
            setSelectedRep('');
            setSelectedStore('');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to assign store.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // const repNameMap = reps.reduce((acc, rep) => ({...acc, [rep.id]: rep.name}), {});
    const storeNameMap = stores.reduce((acc, store) => ({...acc, [store.id]: store.name}), {});

    return (
        <div className={styles.adminCard}>
            <h3 className={styles.adminSectionTitle}>Assign Stores to Sales Reps</h3>
            {message && <div className={styles.adminSuccess}>{message}</div>}
            {error && <div className={styles.adminError}>{error}</div>}

            <form onSubmit={handleAssign} className={styles.adminForm}>
                <div className={styles.formGroup}>
                    <label>Sales Rep:</label>
                    <select value={selectedRep} onChange={(e) => setSelectedRep(e.target.value)} disabled={isSubmitting || reps.length === 0}>
                        <option value="">-- Select Rep --</option>
                        {reps.map(rep => (
                            <option key={rep.id} value={rep.id}>{rep.name}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.formGroup}>
                    <label>Store:</label>
                    <select value={selectedStore} onChange={(e) => setSelectedStore(e.target.value)} disabled={isSubmitting || stores.length === 0}>
                        <option value="">-- Select Store --</option>
                        {stores.map(store => (
                            <option key={store.id} value={store.id}>{store.name}</option>
                        ))}
                    </select>
                </div>
                <button type="submit" className={styles.adminButton} disabled={isSubmitting}>
                    {isSubmitting ? 'Assigning...' : 'Assign Store'}
                </button>
            </form>
            
            <h4 className={styles.adminSubTitle}>Current Assignments</h4>
            <div className={styles.assignmentList}>
                {isLoading && <p>Loading assignments...</p>}
                {!isLoading && Object.keys(assignments).length === 0 && <p>No assignments found.</p>}
                
                {reps.map(rep => {
                    const storeIds = assignments[rep.id] || [];
                    return (
                        <div key={rep.id} className={styles.assignmentRep}>
                            <strong>{rep.name}</strong>
                            <div className={styles.assignmentStoreList}>
                                {storeIds.length > 0 ? storeIds.map(storeId => (
                                    <span key={storeId} className={styles.assignmentStoreTag}>
                                        {storeNameMap[storeId] || storeId}
                                    </span>
                                )) : (
                                    <span className={styles.assignmentStoreTagEmpty}>No stores assigned</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const DataUploader = ({ title, endpoint, onUploadSuccess }) => {
    const [file, setFile] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files ? e.target.files[0] : null;
        if (selectedFile) {
            setFile(selectedFile);
            setMessage('');
            setError('');
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files ? e.dataTransfer.files[0] : null;
        if (droppedFile && droppedFile.type === 'text/csv') {
            setFile(droppedFile);
            setMessage('');
            setError('');
        } else {
            setError('Invalid file type. Please drop a .csv file.');
        }
    };

    const clearFile = () => {
        setFile(null);
        setMessage('');
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            setError('Please select a file to upload.');
            return;
        }

        setIsLoading(true);
        setMessage('');
        setError('');

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(`${API_BASE_URL}${endpoint}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setMessage(response.data.message);
            onUploadSuccess(response.data);
            clearFile(); // Clear file on success
        } catch (err) {
            setError(err.response?.data?.detail || 'An error occurred during upload.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.adminCard}>
            <h3 className={styles.adminSectionTitle}>{title}</h3>
            <form onSubmit={handleSubmit}>
                <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    className={styles.uploadHiddenInput}
                />
                {!file && (
                    <div
                        className={`${styles.uploadDropzone} ${isDragging ? styles.uploadDropzoneDragging : ''}`}
                        onClick={() => fileInputRef.current.click()}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <span>📤</span>
                        <p>Drag & drop a .csv file here, or click to select a file</p>
                    </div>
                )}
                
                {file && (
                    <div className={styles.filePreview}>
                        <span className={styles.fileName}>{file.name}</span>
                        <button type="button" className={styles.fileClearButton} onClick={clearFile} disabled={isLoading}>
                            &times;
                        </button>
                    </div>
                )}

                <button type="submit" className={styles.adminButton} disabled={!file || isLoading}>
                    {isLoading ? 'Uploading...' : 'Upload File'}
                </button>
            </form>
            {message && <div className={styles.adminSuccess}>{message}</div>}
            {error && <div className={styles.adminError}>{error}</div>}
        </div>
    );
};

const AdminPage = () => {
    const [stores, setStores] = useState([]);
    const [reps, setReps] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const fetchAdminData = async () => {
        setIsLoading(true);
        try {
            const [repsRes, storesRes, termsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/sales_reps`),
                axios.get(`${API_BASE_URL}/stores`),
                axios.get(`${API_BASE_URL}/admin/payment_terms`)
            ]);

            const mappedReps = repsRes.data.map(rep => ({
                ...rep,
                id: rep._id
            }));
            
            const mappedStores = storesRes.data.map(store => ({
                ...store,
                id: store._id
            }));

            setReps(mappedReps);
            setStores(mappedStores);
            setPaymentTerms(termsRes.data);
            setError('');
        } catch (err) {
            setError('Failed to load initial admin data. Some components may not work.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAdminData();
    }, []);

    return (
        <div className={styles.adminPageContainer}>
            <div className={styles.adminHeader}>
                <h2 className={styles.adminPageTitle}>Distributor Admin Panel</h2>
                <button onClick={fetchAdminData} className={styles.refreshButton} disabled={isLoading}>
                    {isLoading ? 'Refreshing...' : 'Refresh All Data'}
                </button>
            </div>
            {error && <div className={styles.adminError} style={{marginBottom: '15px'}}>{error}</div>}

            <div className={styles.pageGrid}>
                <CreateUserForm />
                <CreateStoreForm 
                    paymentTerms={paymentTerms} 
                    onStoreCreated={fetchAdminData}
                />

                <PaymentTermsManager
                    terms={paymentTerms}
                    onTermsUpdated={fetchAdminData}
                />

                <StoreAssignment 
                    stores={stores}
                    reps={reps}
                    onAssignmentCreated={fetchAdminData}
                />
                
                <DataUploader 
                    title="Upload Product Data"
                    endpoint="/upload_products"
                    onUploadSuccess={(data) => console.log('Products uploaded:', data)}
                />
                
                <DataUploader 
                    title="Upload Sales Data"
                    endpoint="/upload_sales"
                    onUploadSuccess={(data) => console.log('Sales data uploaded:', data)}
                />
            </div>
        </div>
    );
};

export default AdminPage;